import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { extractErrorMessage } from '../api';
import { fetchAnsweringUser } from '../repositories/authRepository';
import type { User } from '../types';
import { stopViewingAs, viewAsUserId, withoutViewAs } from '../utils/viewAs';

/**
 * While an admin's tab views as another reader (`utils/viewAs`), names them, explains delivery, and offers
 * to stop. Whom comes from `/api/me` asked as the viewed reader, so the server's refusal (not an admin, no
 * such user) shows here.
 */
export function ViewAsBanner() {
  useLocation(); // re-read the tab's view-as on every navigation
  const viewed = viewAsUserId();
  const [who, setWho] = useState<User | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!viewed) return;
    let cancelled = false;
    setWho(null); setError('');
    fetchAnsweringUser()
      .then(user => { if (!cancelled) setWho(user); })
      .catch(err => { if (!cancelled) setError(extractErrorMessage(err)); });
    return () => { cancelled = true; };
  }, [viewed]);

  if (!viewed) return null;
  const stop = () => {
    stopViewingAs();
    // A full load, so nothing the page fetched as the reader stays on screen.
    window.location.assign(window.location.pathname + withoutViewAs(window.location.search));
  };
  return <ViewAsNotice viewed={viewed} who={who} error={error} onStop={stop} />;
}

/** The banner itself: whom the tab views as (or why it cannot), and Stop viewing. */
export function ViewAsNotice({ viewed, who, error, onStop }: {
  viewed: string; who: User | null; error: string; onStop: () => void;
}) {
  return (
    <div className="view-as-banner" role="status">
      <span>
        {error ? <>Can&apos;t view as <code>{viewed}</code>: {error}</>
          : <>Viewing as <strong>{who ? `${who.username} (${who.email})` : viewed}</strong>. Controls are read-only;
            opening For You updates this reader&apos;s delivery.</>}
      </span>
      <button type="button" className="view-as-banner-stop" onClick={onStop}>Stop viewing</button>
    </div>
  );
}
